import { MutableRefObject, useEffect, useRef } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { ensureTrackingSession, trackFlowPopNavigation, trackStepShown } from './tracking';

type TrackingPlace = Parameters<typeof ensureTrackingSession>[0];
type TrackingUser = Parameters<typeof ensureTrackingSession>[1];

/**
 * The booking-flow step shell's analytics side effects (U15), kept out of the layout
 * component so the shell reads as layout only: browser-back parity, the calendar-style
 * session fallback, and per-step `screen_shown`. `navigatingRef` is the shell's
 * header-navigation latch — a header back has already emitted its own event, so the POP
 * listener skips it.
 */
export function useBookingFlowStepTracking({
  emitStepShown,
  place,
  user,
  navigatingRef,
}: {
  emitStepShown: boolean;
  place: TrackingPlace | undefined;
  user: TrackingUser | undefined;
  navigatingRef: MutableRefObject<boolean>;
}): void {
  const history = useHistory();
  const location = useLocation();
  const stepShownPathname = useRef<string>();

  // U15 browser-back parity: emit what the header back button would (cancel on leaving the
  // flow backwards, back events on rewinds). history.listen fires synchronously on the
  // change, before this shell unmounts, so the closed-over pathname still names the step
  // being left. navigatingRef skips header-initiated go() — its click handler has already
  // emitted — and Avbryt's replace never matches POP.
  useEffect(
    () =>
      history.listen((newLocation, action) => {
        if (action !== 'POP' || navigatingRef.current) return;
        trackFlowPopNavigation(location.pathname, newLocation.pathname);
      }),
    [history, location.pathname, navigatingRef],
  );

  // U15: calendar-parity session fallback — a real step render with no active session
  // (deep link, new tab, or a session ended elsewhere) initiates one. Gated like step-shown,
  // so a page about to guard-redirect never initiates for the step it bounced from.
  useEffect(() => {
    if (!emitStepShown || !place) return;
    ensureTrackingSession(place, user ?? null, location.pathname);
  }, [emitStepShown, place, user, location.pathname]);

  // U15: once per pathname, and only on a real step render (emitStepShown) — the spinner
  // shell renders first on the same pathname and must not fire for a step that may still
  // guard-redirect away.
  useEffect(() => {
    if (!emitStepShown || stepShownPathname.current === location.pathname) return;
    stepShownPathname.current = location.pathname;
    trackStepShown(location.pathname);
  }, [emitStepShown, location.pathname]);
}
